/**
 * Board Summaries Section
 * Section 8: Quarterly board pack, enterprise readiness, governance posture
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface BoardSummariesProps {
  data?: {
    quarterlyBoardPack?: {
      revenue: {
        arr: number;
        growth: number;
      };
      customers: {
        total: number;
        enterprise: number;
      };
      reliability: {
        score: number;
        uptime: number;
      };
      governance: {
        soc2Status: string;
        aiActCompliance: boolean;
      };
    };
    enterpriseReadinessScore?: number;
    governancePosture?: {
      soc2: string;
      aiAct: string;
      nist: string;
    };
  };
}

export function BoardSummaries({ data }: BoardSummariesProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Quarterly Board Pack</CardTitle>
          <CardDescription>Executive summary for board review</CardDescription>
        </CardHeader>
        <CardContent>
          {data?.quarterlyBoardPack ? (
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Revenue</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">ARR</div>
                    <div className="text-xl font-bold">{formatCurrency(data.quarterlyBoardPack.revenue.arr)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Growth Rate</div>
                    <div className="text-xl font-bold">{formatPercent(data.quarterlyBoardPack.revenue.growth)}</div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Customers</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Total</div>
                    <div className="text-xl font-bold">{data.quarterlyBoardPack.customers.total}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Enterprise</div>
                    <div className="text-xl font-bold">{data.quarterlyBoardPack.customers.enterprise}</div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Reliability</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Score</div>
                    <div className="text-xl font-bold">{data.quarterlyBoardPack.reliability.score}/100</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Uptime</div>
                    <div className="text-xl font-bold">{formatPercent(data.quarterlyBoardPack.reliability.uptime)}</div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Governance</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={data.quarterlyBoardPack.governance.soc2Status === 'Compliant' ? 'default' : 'destructive'}>
                      SOC2: {data.quarterlyBoardPack.governance.soc2Status}
                    </Badge>
                    <Badge variant={data.quarterlyBoardPack.governance.aiActCompliance ? 'default' : 'destructive'}>
                      AI Act: {data.quarterlyBoardPack.governance.aiActCompliance ? 'Compliant' : 'Non-Compliant'}
                    </Badge>
                  </div>
                </div>
              </div>

              <Button>Export Board Pack</Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Board pack data will be generated here.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Enterprise Readiness Score</CardTitle>
            <CardDescription>Overall readiness for enterprise sales</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{data?.enterpriseReadinessScore ?? 0}/100</div>
            <Badge variant={(data?.enterpriseReadinessScore ?? 0) >= 80 ? 'default' : 'destructive'} className="mt-2">
              {(data?.enterpriseReadinessScore ?? 0) >= 80 ? 'Ready' : 'Needs Improvement'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Governance Posture</CardTitle>
            <CardDescription>Compliance status across standards</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.governancePosture ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">SOC2</span>
                  <Badge variant="default">{data.governancePosture.soc2}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">AI Act</span>
                  <Badge variant="default">{data.governancePosture.aiAct}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">NIST</span>
                  <Badge variant="default">{data.governancePosture.nist}</Badge>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Governance data loading...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
